import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tabs from '@/components/Tabs'
import { AuthType } from '@/components/Tabs/index.type'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
// import OtherLoginItems from '@/components/OtherLoginItems'
import { Toast } from 'react-vant';
import { useTranslation } from 'react-i18next'
import { useSendCode } from '@/hooks/useSendCode'
import { APIWeb } from '@/api'

export default React.memo(function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<AuthType>(AuthType.Email)
  const [email, setEmail] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [errors, setErrors] = useState<{ email?: string, inviteCode?: string }>({})
  const { sendCode, sendCodeLoading } = useSendCode()

  // 验证邮箱格式
  const validateEmail = useCallback((emailValue: string) => {
    if (!emailValue) {
      return t('请输入邮箱地址')
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      return t('请输入有效的邮箱地址')
    }
    return ''
  }, [t])

  // 验证邀请码格式（必填，但不限制格式）
  const validateInviteCode = useCallback((code: string) => {
    if (!code || code.trim() === '') {
      return t('请输入邀请码')
    }
    return ''
  }, [t])

  // 处理邮箱输入变化
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setErrors(prev => ({ ...prev, email: error }))
  }, [validateEmail])

  // 处理邀请码输入变化
  const handleInviteCodeChange = useCallback((value: string) => {
    setInviteCode(value)
    const error = validateInviteCode(value)
    setErrors(prev => ({ ...prev, inviteCode: error }))
  }, [validateInviteCode])

  // 切换登录注册类型
  const onChange = useCallback((type: AuthType) => {
    if (type === AuthType.Phone) {
      Toast.info({
        message: t('当前地区暂不支持手机号登录注册'),
        position: 'middle'
      })
      return
    }
    setSelectedType(type)
  }, [t])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    if (selectedType === AuthType.Email) {
      return email && inviteCode && !validateEmail(email) && !validateInviteCode(inviteCode)
    }
    return false // 手机注册暂未实现
  }, [selectedType, email, inviteCode, validateEmail, validateInviteCode])

  // 处理注册提交
  const handleSendCode = useCallback(async () => {
    if (selectedType === AuthType.Email) {
      const emailError = validateEmail(email)
      const inviteCodeError = validateInviteCode(inviteCode)

      if (emailError || inviteCodeError) {
        setErrors({ email: emailError, inviteCode: inviteCodeError })
        return
      }
      
      // 提交前检查账号是否已注册
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const response = await APIWeb.checkAccountStatus({ account: email })
        Toast.clear()
        if (response?.registered) {
          Toast.info(t('该邮箱已被注册'))
          return
        }
      } catch (error) {
        console.error('检测账号状态失败:', error)
        // 检测失败时允许继续发送验证码
      }
      
      // 提交前检查邀请码是否有效
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const result = await APIWeb.checkInviteCode({ invite_code: inviteCode })
        Toast.clear()
        if (!result?.valid) {
          Toast.info(t('邀请码无效'))
          return
        }
      } catch (error) {
        console.error('检测邀请码失败:', error)
        Toast.info(t('邀请码无效'))
        return
      }
      
      const data = await sendCode({ account: email, scene: 'register' })
      if (data) {
        navigate(`/register/verification-code`, {
          state: {
            ...data,
            inviteCode // 传递邀请码
          }
        })
        setEmail('')
        setInviteCode('')
      }
    }
  }, [navigate, selectedType, email, inviteCode, validateEmail, validateInviteCode, sendCode, t])

  const EmailRegisterView = useMemo(
    () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('您的电子邮箱')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.email)}
            value={email}
            onChangeText={handleEmailChange}
            placeholder={t('请输入您的电子邮箱')}
            inputMode='email'
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('邀请码')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.inviteCode)}
            value={inviteCode}
            onChangeText={handleInviteCodeChange}
            placeholder={t('请输入邀请码')}
          />
          {errors.inviteCode && (
            <p className="mt-1 text-xs text-red-600">{errors.inviteCode}</p>
          )}
        </div>
      </div>
    ),
    [email, inviteCode, handleEmailChange, handleInviteCodeChange, errors.email, errors.inviteCode, t]
  )

  const PhoneRegisterView = useMemo(
    () => (
      <div className="text-5xl text-gray-200 text-center my-20">
        {t('即将上线')}
      </div>
    ),
    [t]
  )

  const RegisterViewRender = useMemo(
    () => (
      selectedType === AuthType.Email ? EmailRegisterView : PhoneRegisterView
    ),
    [selectedType, EmailRegisterView, PhoneRegisterView]
  )

  return (
    <div className="bg-white h-full">
      <div className="overflow-y-auto">
        <div>
          {/* Logo */}

          <div className="w-full px-6 text-center text-[#1A1A1A] flex flex-col items-center justify-center pt-14 pb-8 text-2xl">
            <img className='w-34 mb-5' src="/icon/logo.svg" alt="logo" />
            {t('创建WPAY账户')}
          </div>

          <div className="px-6">
            <Tabs
              items={[
                { type: AuthType.Email, text: t('邮箱注册') },
                { type: AuthType.Phone, text: t('手机注册') }
              ]}
              defaultSelected={AuthType.Email}
              onChange={onChange}
            />
          </div>

          <div className="px-6 mt-8">
            {RegisterViewRender}
          </div>

          <div className="px-6 mt-8">
            <CustomButton
              onClick={handleSendCode}
              loading={sendCodeLoading}
              disabled={sendCodeLoading || !isFormValid}
            >
              {t('注册')}
            </CustomButton>
          </div>

          <div className="px-6 mt-6 flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">{t('已有账户')}</span>
            <Link to="/login" className="text-sm text-[var(--primary-color)]">
              {t('登录')}
            </Link>
          </div>
        </div>
        {/* Other register options */}
        {/* <div className="px-6 mt-12 pb-8">
          <OtherLoginItems />
        </div> */}
      </div>
    </div>
  )
})